import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/context/AuthContext';
import { getFilterPresets, createFilterPreset, deleteFilterPreset, markPresetUsed, updateFilterPreset } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import { Bookmark, ChevronDown, Save, Trash2, Edit2, Clock, Loader2 } from 'lucide-react';

const MAX_PRESETS = 10;

// Filter keys to save in presets (excluding search, sort, and page)
const PRESET_FILTER_KEYS = [
  'category_id', 'site_id', 'country', 'age_bucket', 'age_buckets',
  'manufacturer', 'min_width', 'max_width', 'min_thickness', 'max_thickness',
  'core_diameters', 'certifications', 'customer_specific', 'compliance_flag',
  'min_quantity', 'quantity_unit', 'min_price', 'max_price'
];

// Check if filters have any active values (worth saving)
const hasActiveFilters = (filters) => {
  return PRESET_FILTER_KEYS.some(key => {
    const value = filters[key];
    if (value === '' || value === undefined || value === null) return false;
    if (key === 'quantity_unit' && value === 'sqm') return false;
    return true;
  });
};

// Extract only preset-relevant filters
const extractPresetFilters = (filters) => {
  const preset = {};
  PRESET_FILTER_KEYS.forEach(key => {
    const value = filters[key];
    if (value !== '' && value !== undefined && value !== null) {
      if (key === 'quantity_unit' && value === 'sqm') return; // Skip default
      preset[key] = value;
    }
  });
  return preset;
};

// Count active filters in a preset
const countActiveFilters = (filters) => {
  return Object.keys(filters).filter(key => {
    const value = filters[key];
    return value !== '' && value !== undefined && value !== null;
  }).length;
};

export const FilterPresetManager = ({ filters, onApplyPreset, className }) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  
  const [presets, setPresets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  
  // Dialog states
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [renameDialogOpen, setRenameDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedPreset, setSelectedPreset] = useState(null);
  const [presetName, setPresetName] = useState('');

  // Load presets on mount
  useEffect(() => {
    if (user) {
      loadPresets();
    }
  }, [user]);

  const loadPresets = async () => {
    try {
      setLoading(true);
      const response = await getFilterPresets();
      setPresets(response.data);
    } catch (error) {
      console.error('Failed to load presets:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSavePreset = async () => {
    if (!presetName.trim()) {
      toast.error(t('preset.nameRequired', 'Please enter a preset name'));
      return;
    }

    try {
      setSaving(true);
      const presetFilters = extractPresetFilters(filters);
      await createFilterPreset({ name: presetName.trim(), filters: presetFilters });
      toast.success(t('preset.saved', 'Preset saved'));
      setSaveDialogOpen(false);
      setPresetName('');
      loadPresets();
    } catch (error) {
      const message = error.response?.data?.detail || t('preset.saveFailed', 'Failed to save preset');
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const handleApplyPreset = async (preset) => {
    try {
      // Mark as used (fire-and-forget)
      markPresetUsed(preset.preset_id).catch(() => {});
      
      // Apply filters
      onApplyPreset(preset.filters);
      toast.success(t('preset.applied', 'Preset "{name}" applied').replace('{name}', preset.name));
      
      // Update local state to move to top
      setPresets(prev => {
        const updated = prev.filter(p => p.preset_id !== preset.preset_id);
        return [{ ...preset, last_used_at: new Date().toISOString() }, ...updated];
      });
    } catch (error) {
      toast.error(t('preset.applyFailed', 'Failed to apply preset'));
    }
  };

  const handleRenamePreset = async () => {
    if (!presetName.trim() || !selectedPreset) return;

    try {
      setSaving(true);
      await updateFilterPreset(selectedPreset.preset_id, { name: presetName.trim() });
      toast.success(t('preset.renamed', 'Preset renamed'));
      setRenameDialogOpen(false);
      setSelectedPreset(null);
      setPresetName('');
      loadPresets();
    } catch (error) {
      const message = error.response?.data?.detail || t('preset.renameFailed', 'Failed to rename preset');
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const handleDeletePreset = async () => {
    if (!selectedPreset) return;

    try {
      await deleteFilterPreset(selectedPreset.preset_id);
      toast.success(t('preset.deleted', 'Preset deleted'));
      setDeleteDialogOpen(false);
      setSelectedPreset(null);
      loadPresets();
    } catch (error) {
      toast.error(t('preset.deleteFailed', 'Failed to delete preset'));
    }
  };

  const openRenameDialog = (preset, e) => {
    e.stopPropagation();
    setSelectedPreset(preset);
    setPresetName(preset.name);
    setRenameDialogOpen(true);
  };

  const openDeleteDialog = (preset, e) => {
    e.stopPropagation();
    setSelectedPreset(preset);
    setDeleteDialogOpen(true);
  };

  // Don't render if user is not logged in
  if (!user) return null;

  const canSave = hasActiveFilters(filters) && presets.length < MAX_PRESETS;

  return (
    <div className={className} data-testid="filter-preset-manager">
      <div className="flex items-center gap-2">
        {/* Preset Dropdown */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button 
              variant="outline" 
              size="sm" 
              className="h-8 flex-1 justify-between text-sm"
              disabled={loading}
              data-testid="preset-dropdown-trigger"
            >
              <span className="flex items-center gap-2">
                <Bookmark className="h-3.5 w-3.5" />
                {t('preset.presets', 'Presets')}
                {presets.length > 0 && (
                  <Badge variant="secondary" className="h-5 min-w-5 px-1 text-xs">
                    {presets.length}
                  </Badge>
                )}
              </span>
              <ChevronDown className="h-3.5 w-3.5 opacity-50" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-64">
            {loading ? (
              <div className="flex items-center justify-center py-4">
                <Loader2 className="h-4 w-4 animate-spin" />
              </div>
            ) : presets.length === 0 ? (
              <div className="px-3 py-4 text-center text-sm text-muted-foreground">
                {t('preset.noPresets', 'No saved presets')}
                <p className="text-xs mt-1">{t('preset.saveHint', 'Apply filters and save them')}</p>
              </div>
            ) : (
              <>
                {presets.map((preset) => (
                  <DropdownMenuItem 
                    key={preset.preset_id} 
                    className="flex items-center justify-between cursor-pointer group"
                    onSelect={() => handleApplyPreset(preset)}
                    data-testid={`preset-item-${preset.preset_id}`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="font-medium truncate">{preset.name}</div>
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <span>{countActiveFilters(preset.filters)} {t('preset.filters', 'filters')}</span>
                        {preset.last_used_at && (
                          <>
                            <span>•</span>
                            <Clock className="h-3 w-3" />
                          </>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button
                        onClick={(e) => openRenameDialog(preset, e)}
                        className="p-1 hover:bg-accent rounded"
                        title={t('preset.rename', 'Rename')}
                      >
                        <Edit2 className="h-3 w-3" />
                      </button>
                      <button
                        onClick={(e) => openDeleteDialog(preset, e)}
                        className="p-1 hover:bg-destructive/10 hover:text-destructive rounded"
                        title={t('preset.delete', 'Delete')}
                      >
                        <Trash2 className="h-3 w-3" />
                      </button>
                    </div>
                  </DropdownMenuItem>
                ))}
                <DropdownMenuSeparator />
                <div className="px-2 py-1.5 text-xs text-muted-foreground">
                  {presets.length}/{MAX_PRESETS} {t('preset.used', 'presets used')}
                </div>
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>

        {/* Save Button */}
        <Button
          variant="outline"
          size="sm"
          className="h-8"
          onClick={() => setSaveDialogOpen(true)}
          disabled={!canSave}
          title={!hasActiveFilters(filters) 
            ? t('preset.noFiltersToSave', 'No active filters to save')
            : presets.length >= MAX_PRESETS 
              ? t('preset.maxReached', 'Maximum presets reached')
              : t('preset.saveCurrentFilters', 'Save current filters')
          }
          data-testid="save-preset-btn"
        >
          <Save className="h-3.5 w-3.5" />
        </Button>
      </div>

      {/* Save Dialog */}
      <Dialog open={saveDialogOpen} onOpenChange={setSaveDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t('preset.savePreset', 'Save Filter Preset')}</DialogTitle>
            <DialogDescription>
              {t('preset.saveDescription', 'Give your filter combination a name to quickly apply it later.')}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="preset-name">{t('preset.name', 'Preset Name')}</Label>
              <Input
                id="preset-name"
                placeholder={t('preset.namePlaceholder', 'e.g., FSC Films > 100mm')}
                value={presetName}
                onChange={(e) => setPresetName(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSavePreset()}
                data-testid="preset-name-input"
              />
            </div>
            <div className="text-sm text-muted-foreground">
              {countActiveFilters(extractPresetFilters(filters))} {t('preset.filtersWillBeSaved', 'filters will be saved')}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSaveDialogOpen(false)}>
              {t('common.cancel', 'Cancel')}
            </Button>
            <Button onClick={handleSavePreset} disabled={saving || !presetName.trim()}>
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {t('common.save', 'Save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Rename Dialog */}
      <Dialog open={renameDialogOpen} onOpenChange={setRenameDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t('preset.renamePreset', 'Rename Preset')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="rename-preset">{t('preset.newName', 'New Name')}</Label>
              <Input
                id="rename-preset"
                value={presetName}
                onChange={(e) => setPresetName(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleRenamePreset()}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRenameDialogOpen(false)}>
              {t('common.cancel', 'Cancel')}
            </Button>
            <Button onClick={handleRenamePreset} disabled={saving || !presetName.trim()}>
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {t('common.save', 'Save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('preset.deleteConfirm', 'Delete Preset?')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('preset.deleteDescription', 'This will permanently delete the preset "{name}".')
                .replace('{name}', selectedPreset?.name || '')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel', 'Cancel')}</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDeletePreset}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {t('common.delete', 'Delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default FilterPresetManager;
